import { Route, Routes, Navigate, useLocation } from "react-router-dom";
import "./App.css";
import SignIn from "./pages/Authentication/SignIn";
import SignUp from "./pages/Authentication/SignUp";
import MainLayout from "./pages/Layouts/MainLayout";
import Roster from "./pages/Roster/Roster";
import Dashboard from "./pages/Dashboard/Dashboard";
import Profile from "./pages/Profile/Profile";
import Timesheet from "./pages/Timesheet/Timesheet";
import ManageRoster from "./pages/ManageRoster/ManageRoster";
import Home from "./pages/Home";
import { jwtDecode } from "jwt-decode";
import SessionTimeout from "./pages/SessionTimeOut";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { setApiNavigate } from "./api/api";
import Attendance from "./pages/QRCode/Attendance";
import CheckOut from "./pages/QRCode/CheckOut";
import NotFound from "./pages/NotFound";
import ManageBusinesses from "./pages/ManageBusinesses/ManageBusinesses";
import Leave from "./pages/Leave/Leave";
import YourBusiness from "./pages/YourBusiness/YourBusiness";
import WeeklyTimeSheet from "./pages/WeeklyTimeSheet/WeeklyTimeSheet";
import ManageEmployees from "./pages/ManageEmployees/ManageEmployees";

const isLoggedInWithValidToken = () => {
  const token = localStorage.getItem("token");

  if (!token) return false;

  try {
    const decoded = jwtDecode(token);
    const currentTime = Date.now() / 1000; // Convert to seconds
    return decoded.exp > currentTime; // Check if the token is still valid
  } catch (error) {
    console.error("Invalid token:", error);
    return false;
  }
};

const ProtectedRoute = ({ children, allowedRoles = null }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState(
    isLoggedInWithValidToken()
  );
  const [hadValidToken, setHadValidToken] = useState(
    !!localStorage.getItem("token")
  );

  useEffect(() => {
    const checkTokenAndRole = () => {
      const validToken = isLoggedInWithValidToken();
      const tokenExists = !!localStorage.getItem("token");

      if (!validToken) {
        setIsAuthenticated(false);
        if (hadValidToken && !tokenExists) {
          // Token was valid but now expired or removed
          localStorage.removeItem("token");
          localStorage.removeItem("userDetails"); // Fixed typo
          navigate("/session-timeout", { replace: true });
        } else {
          // No valid token from the start
          navigate("/sign-in", {
            replace: true,
            state: { from: location },
          });
        }
        return;
      }

      // Token is valid, now check roles if specified
      if (allowedRoles) {
        const userDetails =
          JSON.parse(localStorage.getItem("userDetails")) || {};
        const userRole = userDetails.accountType;

        if (!userRole || !allowedRoles.includes(userRole)) {
          navigate("/not-found", { replace: true });
          return;
        }
      }

      // Token is valid and role (if required) matches
      setIsAuthenticated(true);
      setHadValidToken(true);
    };

    checkTokenAndRole();
    const intervalId = setInterval(checkTokenAndRole, 10000); // Check every 10 seconds
    return () => clearInterval(intervalId);
  }, [navigate, location, allowedRoles]);

  return isAuthenticated ? (
    children
  ) : (
    <Navigate to="/session-timeout" replace />
  );
};

function App() {
  const navigate = useNavigate();

  useEffect(() => {
    setApiNavigate(navigate); // Pass navigate to api.js
  }, [navigate]);

  return (
    <>
      <Routes>
        <Route index element={<Home />} />

        <Route path="sign-in" element={<SignIn />} />
        <Route path="sign-up" element={<SignUp />} />
        <Route path="session-timeout" element={<SessionTimeout />} />
        <Route path="not-found" element={<NotFound />} />
        <Route path="*" element={<NotFound />} />

        <Route
          path="/"
          element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          }
        >

          <Route path="dashboard" element={<Dashboard />} />
          <Route path="profile" element={<Profile />} />
          {/* >>>>>>>>>>>>>>>> Employee Routes >>>>>>>>>>>>>>>> */}
          <Route
            path="roster"
            element={
              <ProtectedRoute allowedRoles={["EMPLOYEE"]}>
                <Roster />
              </ProtectedRoute>
            }
          />
          <Route
            path="timesheet"
            element={
              <ProtectedRoute allowedRoles={["EMPLOYEE"]}>
                <Timesheet />
              </ProtectedRoute>
            }
          />
          <Route
            path="leave"
            element={
              <ProtectedRoute allowedRoles={["EMPLOYEE"]}>
                <Leave />
              </ProtectedRoute>
            }
          />
          <Route
            path="attendance"
            element={
              <ProtectedRoute allowedRoles={["EMPLOYEE"]}>
                <Attendance />
              </ProtectedRoute>
            }
          />
          <Route
            path="check-out"
            element={
              <ProtectedRoute allowedRoles={["EMPLOYEE"]}>
                <CheckOut />
              </ProtectedRoute>
            }
          />
          {/* <<<<<<<<<<<<<<<< Employee Routes <<<<<<<<<<<<<<<< */}

          {/* >>>>>>>>>>>>>>>> Business Owner Routes >>>>>>>>>>>>>>>> */}
          <Route
            path="manage-roster"
            element={
              <ProtectedRoute allowedRoles={["BUSINESSOWNER"]}>
                <ManageRoster />
              </ProtectedRoute>
            }
          />
          <Route
            path="manage-employees"
            element={
              <ProtectedRoute allowedRoles={["BUSINESSOWNER"]}>
                <ManageEmployees />
              </ProtectedRoute>
            }
          />
          <Route
            path="weekly-timesheet"
            element={
              <ProtectedRoute allowedRoles={["BUSINESSOWNER"]}>
                <WeeklyTimeSheet />
              </ProtectedRoute>
            }
          />
          <Route
            path="your-business"
            element={
              <ProtectedRoute allowedRoles={["BUSINESSOWNER"]}>
                <YourBusiness />
              </ProtectedRoute>
            }
          />
          {/* <<<<<<<<<<<<<<<< Business Owner Routes <<<<<<<<<<<<<<<< */}

          {/* >>>>>>>>>>>>>>>> Admin Routes >>>>>>>>>>>>>>>> */}
          <Route
            path="manage-businesses"
            element={
              <ProtectedRoute allowedRoles={["ADMIN"]}>
                <ManageBusinesses />
              </ProtectedRoute>
            }
          />
          {/* <<<<<<<<<<<<<<<< Admin Routes <<<<<<<<<<<<<<<< */}
        </Route>
      </Routes>
    </>
  );
}

export default App;
